<template>
  <div class="user-info-card">
    <h3 class="mb-3 text-base font-semibold">{{ $t("userinfo.title") }}</h3>

    <form novalidate @submit.prevent="form.handleSubmit">
      <div class="grid grid-cols-2 gap-x-6">
        <form.Field name="firstName" v-slot="{ field }">
          <SField :data-invalid="showError(field) || undefined" class="mb-4">
            <SFieldLabel :for="field.name">
              {{ $t("userinfo.first-name") }}
            </SFieldLabel>
            <SInput
              :id="field.name"
              :model-value="field.state.value"
              :aria-invalid="showError(field) || undefined"
              @update:model-value="field.handleChange"
              @blur="field.handleBlur"
            />
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <form.Field name="lastName" v-slot="{ field }">
          <SField :data-invalid="showError(field) || undefined" class="mb-4">
            <SFieldLabel :for="field.name">
              {{ $t("userinfo.last-name") }}
            </SFieldLabel>
            <SInput
              :id="field.name"
              :model-value="field.state.value"
              :aria-invalid="showError(field) || undefined"
              @update:model-value="field.handleChange"
              @blur="field.handleBlur"
            />
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <form.Field name="languageId" v-slot="{ field }">
          <SField :data-invalid="showError(field) || undefined" class="mb-4">
            <SFieldLabel>{{ $t("userinfo.language") }}</SFieldLabel>
            <SelectLanguages
              :model-value="field.state.value"
              @update:model-value="field.handleChange"
            />
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>
      </div>

      <div class="mt-2 flex justify-end">
        <SButton
          type="submit"
          size="lg"
          :loading="isSubmitting"
          :disabled="!isDirty || isSubmitting"
        >
          {{ $t("button.save") }}
        </SButton>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import {
  SButton,
  SField,
  SFieldError,
  SFieldLabel,
  SInput,
} from "@smeetz/ui-core";

import { useSmeetzForm } from "@smeetz/services-validation";

import UserModule from "@/store/modules/user";
import ModalModule from "@/store/modules/modal";
import { IUser } from "@/interfaces/responses/user";
import { IUpdateUserInfo } from "@/interfaces/_shared/user";
import { convertCodeToIdLang, convertIdToCodeLang } from "@/utils/helpers/lang";
import { ModalStatus } from "@/constants/modal";

import SelectLanguages from "@/components/form-items/selects/select-languages/SelectLanguages.vue";

import { userInfoSchema, type UserInfoValues } from "./user-info.form";

const user = computed<IUser>(() => UserModule.getUser || ({} as IUser));

const { form, showError, firstErrorText } = useSmeetzForm<UserInfoValues>({
  defaultValues: {
    firstName: user.value.firstName,
    lastName: user.value.lastName,
    languageId: convertCodeToIdLang(user.value.language?.code),
    languageCode: user.value.language?.code || "",
  },
  schema: userInfoSchema(),
  validatorTiming: "onSubmit",
  onSubmit: async ({ value }) => {
    await UserModule.patchInfo({
      ...value,
      languageCode: convertIdToCodeLang(Number(value.languageId)),
    } as unknown as IUpdateUserInfo);
    await UserModule.fetchUser();
    ModalModule.setStatus(ModalStatus.Hidden);
  },
});

const isSubmitting = form.useStore((state) => state.isSubmitting);
const isDirty = form.useStore((state) => state.isDirty);
</script>
